import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {CarePlanServiceDgSerachResults} from "../models/datagrid-search-results/care-plan-service.dg-serach-results.model";
import {CarePlanService} from "../models/care-plan-service.model";
import {CarePlanServiceGroupType} from "../models/care-plan-service-group-type.model";

/**
 * Serwis warstwy danych zarządzający usługami abonamentowymi (Care Plan Services).
 * Odpowiada za zasilanie tabel DataGrid (operacje CRUD, paginacja, wyszukiwanie)
 * oraz pobieranie usług i przypisanych im kategorii w kontekście konkretnego klienta.
 */
@Injectable({
  providedIn: 'root'
})
export class CarePlanServicesService implements DataGridServiceInterface<Observable<CarePlanServiceDgSerachResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę usług przypisanych do planów opieki,
   * zasilając główny widok tabeli DataGrid.
   *
   * @param limit Limit ilości rekordów na stronę.
   * @param offset Przesunięcie względem początku listy.
   * @param sortOrder Kierunek sortowania ('ASC' / 'DESC').
   * @param orderBy Nazwa pola, według którego sortowane są dane.
   * @param showDeletedObjects Flaga określająca widoczność usuniętych usług (Archiwum).
   * @returns Observable emitujący ustandaryzowany obiekt wyników wyszukiwania DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<CarePlanServiceDgSerachResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/care-plan-services/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new CarePlanServiceDgSerachResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new CarePlanService(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);

      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje zapytanie filtrujące (wyszukiwanie zaawansowane) po usługach z planów opieki.
   *
   * @param limit Limit ilości rekordów.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa par klucz-wartość reprezentująca kryteria wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa pola sortującego.
   * @param showDeletedObjects Flaga widoczności usuniętych obiektów.
   * @returns Observable emitujący przefiltrowaną listę obiektów dla tabeli.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<CarePlanServiceDgSerachResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/care-plan-services/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new CarePlanServiceDgSerachResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new CarePlanService(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa wskazaną usługę z systemu.
   *
   * @param uuid Globalnie unikalny identyfikator usuwanej usługi.
   * @returns Observable zwracający status ('ok') przy sukcesie.
   */
  delete(uuid: any) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/care-plan-services/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'ok';
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Dodaje nową usługę do słownika usług abonamentowych.
   *
   * @param carePlanService Model reprezentujący nowo tworzoną usługę.
   * @returns Observable emitujący zinstancjonowany obiekt usługi (z przypisanym UUID z serwera).
   */
  addCarePlanService(carePlanService: CarePlanService) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/care-plan-services', carePlanService).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<CarePlanService>(new CarePlanService(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje zmiany w istniejącej usłudze abonamentowej.
   *
   * @param carePlanService Model usługi ze zaktualizowanymi polami.
   * @returns Observable emitujący poprawnie zaktualizowany obiekt usługi.
   */
  updateCarePlanService(carePlanService: CarePlanService) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/care-plan-services', carePlanService).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<CarePlanService>(new CarePlanService(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę wszystkich usług abonamentowych (CarePlanService)
   * przypisanych do konkretnego klienta.
   * @param uuid Identyfikator klienta.
   * @returns Observable emitujący pełną tablicę usług przypisanych do tego klienta.
   */
  getCarePlanServiceForClient(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/care-plan-services/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new CarePlanService(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę typów grup usług (CarePlanServiceGroupType) zdefiniowanych
   * dla danego klienta, pozwalając na wyrenderowanie słowników (np. list rozwijanych)
   * podczas tworzenia nowej usługi.
   *
   * @param uuid Identyfikator klienta.
   * @returns Observable emitujący tablicę zdefiniowanych grup usług.
   */
  getCarePlanServiceGroupTypes(uuid) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/care-plan-service-group-types/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new CarePlanServiceGroupType(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

}
